import React, {useEffect, useRef, useState} from "react";
import {TextField, Button, Typography, CircularProgress, Checkbox} from "@material-ui/core";
import {CardNumberElement, CardExpiryElement, CardCvcElement, useElements, useStripe} from "@stripe/react-stripe-js"
import StripeInput from "./stripe-input";
import Info from "@material-ui/icons/Info";
import {useSnackbar} from "notistack";
import * as toast from "../../utils/toast";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import {scroller} from "react-scroll";
import {StripeCardError} from "../index";

const CardForm = ({onSubmit, cardError, options={storageInfo:false, storeCheckbox: false, buttonTitle: 'Submit', dense: true} }) => {
    const elements = useElements();
    const stripe = useStripe();
    const [state, setState] = useState({
        cardholder: {error: '', complete: false, value: ''},
        cardNumber: {error: '', complete: false},
        cardExpiry: {error: '', complete: false},
        cardCvc:    {error: '', complete: false},
        done: false,
        saveCard:   false,
        isFetching: false,
    })
    const {enqueueSnackbar} = useSnackbar();     // Snackbar Hook for notifications

    // Styling for stripe elements
    const elOptions = {
        style: {
            base: {
                fontFamily: 'Mulish',
                fontSize: '16px',
            }
        }
    }

    // Scroll to the payment method section so the 'Buy' button
    // is still visible when we show the storage information info
    useEffect(() => {
        if (state.saveCard) {
            scroller.scrollTo('payment-method', {
                duration    : 800,
                delay       : 200,
                smooth      : 'easeInOutQuart',
                containerId : 'psb',
            });
        }

    },[state.saveCard])

    // Track our mounted state so we don't try and setState() after we're gone
    let isMounted = useRef(true)
    useEffect(() => {
        // React calls the return function when the component is unmounted
        return () => {isMounted.current = false}
    },[])

    // If stripe.js hasn't loaded yet, we'll get a null value
    if (!elements || !stripe) {
        return null
    }

    const canSubmit = () => {
        return state.cardholder.complete &&
            state.cardNumber.complete &&
            state.cardExpiry.complete &&
            state.cardCvc.complete
    }

    const onCardholderChange = (e) => {
        updateCardholder(e.target.value);
    }

    const validateCardholder = () => {
        updateCardholder(state.cardholder.value);
    }

    const updateCardholder = (value) => {
        const cardholder = {
            error: value.length === 0 ? 'Please enter the cardholder name' : '',
            complete: value.length > 0,
            value: value,
        }
        setState({...state, cardholder})
    }

    const onElementChange = (e) => {
        const err = e.error ? e.error.message : '';
        setState({...state, [e.elementType]: {error: err, complete: e.complete}})
    }

    const onFormSubmit = () => {
        // Check the cardholder name is entered
        if (!state.cardholder.complete) {
            setState({...state, cardholder: {...state.cardholder, error: 'Please enter the cardholder name'}})
            return
        }
        const cardElement = elements.getElement(CardNumberElement);
        setState({...state, isFetching: true})

        stripe.createToken(cardElement, {name: state.cardholder.value})
            .then((result) => {
                const {error, token} = result;
                if (token) {
                    // Call the submit callback passing the token back.
                    // onSubmit should be a promise that is resolved when the parent has finished processing.
                    // We could use it to turn off isFetching if we were still displayed, but this component
                    // is unmounted when we're done either way
                    if (typeof(onSubmit) === 'function') {
                        //console.log('CardForm calling onSubmit');
                        onSubmit({token, saveCard: state.saveCard})
                            .then(() => {
                                //console.log('CardForm .then() invoked');
                                if (isMounted.current) {
                                    //console.log('CardForm .then() resetting isFetching');
                                    setState({...state, isFetching: false})
                                }
                            })
                    }
                } else {
                    // Stripe error object properties:
                    // {
                    //      type: api_connection_error || api_error || authentication_error || card_error ||
                    //            idempotency_error || invalid_request_error || rate_limit_error
                    //      code: e.g. authentication_required, expired_card
                    //      decline_code: if the error is because of a card_error
                    //      message: human readable string. if card_error can show to user.
                    // }
                    if (error.type === 'validation_error') {
                        if (error.code.indexOf('number') !== -1) {
                            state.cardNumber.error = error.message
                        } else if (error.code.indexOf('expiry') !== -1) {
                            state.cardExpiry.error = error.message
                        } else if (error.code.indexOf('cvc') !== -1) {
                            state.cardCvc.error = error.message
                        }
                    } else if (error.type === 'card_error') {
                        const err = {status: error.type , statusText: error.decline_code ? error.code + ":" + error.decline_code : error.code }
                        toast.apiError(error.message, err, enqueueSnackbar)
                    } else {
                        const err = {status: error.type , statusText: error.code}
                        toast.apiError("Sorry, something's gone wrong at our end. You have not been charged.", err, enqueueSnackbar);
                    }
                    setState({...state, isFetching: false})
                }
            })
    }

    const handleCheck = (e) => {
        setState({...state, saveCard: e.target.checked})
    }

    return (
        <div className="w-full">

            <TextField
                label="Cardholder Name"
                name="cardholderName"
                variant="outlined"
                placeholder="Name"
                InputLabelProps={{shrink: true}}
                required
                fullWidth
                margin={options.dense ? 'dense':'normal'}
                value={state.cardholder.value}
                onChange={onCardholderChange}
                onBlur={validateCardholder}
                helperText={<span style={{color: 'red'}}>{state.cardholder.error}</span>}
            />

            <TextField
                label="Card Number"
                name="cardNumber"
                variant="outlined"
                required
                fullWidth
                InputLabelProps={{shrink: true}}
                InputProps={{
                    inputComponent: StripeInput,
                    inputProps: {
                        component: CardNumberElement,
                        options: elOptions,
                        onChange: onElementChange,
                    },
                }}
                helperText={<span style={{color: 'red'}}>{state.cardNumber.error}</span>}
                margin={options.dense ? 'dense':'normal'}
            />

            <div className="flex flex-row space-x-3">
                <TextField
                    label="Expiry Date"
                    name="cardExpiry"
                    variant="outlined"
                    required
                    fullWidth
                    InputLabelProps={{shrink: true}}
                    InputProps={{
                        inputComponent: StripeInput,
                        inputProps: {
                            component: CardExpiryElement,
                            options: elOptions,
                            onChange: onElementChange,
                        },
                    }}
                    helperText={<span style={{color: 'red'}}>{state.cardExpiry.error}</span>}
                    margin={options.dense ? 'dense':'normal'}
                />

                <TextField
                    label="CVC"
                    name="cardCvc"
                    variant="outlined"
                    required
                    fullWidth
                    InputLabelProps={{shrink: true}}
                    InputProps={{
                        inputComponent: StripeInput,
                        inputProps: {
                            component: CardCvcElement,
                            options: elOptions,
                            onChange: onElementChange,
                        },
                    }}
                    helperText={<span style={{color: 'red'}}>{state.cardCvc.error}</span>}
                    margin={options.dense ? 'dense':'normal'}
                />
            </div>

            { options.storeCheckbox ?
                <FormControlLabel
                    label={<Typography variant="body2">Update my account to use this card</Typography>}
                    margin={options.dense ? 'dense':'normal'}
                    control={
                        <Checkbox name="checkTerms" color="primary" onChange={handleCheck}/>
                    }
                />
                : null
            }

            { options.storageInfo || state.saveCard ?
                <div className="flex flex-row">
                    <Info fontSize="small" color="primary"/>
                    <div className="ml-2 text-xs">
                        Your card information is securely stored by our payments
                        partner <a href="https://stripe.com" target="_blank" rel="noopener noreferrer">
                        <strong>Stripe</strong>
                    </a>, one of the world's leading online
                        payment providers. We do not store your card details on our servers.
                    </div>
                </div>
                : null
            }

            <StripeCardError error={cardError}/>

            <Button
                type="submit"
                variant="contained"
                color="secondary"
                onClick={onFormSubmit}
                disabled={state.isFetching}
                style={{width: '100%', marginTop: 12}}
            >
                {state.isFetching ? <CircularProgress size={24} /> : options.buttonTitle }
            </Button>
        </div>
    )
}

export default CardForm;